/* eslint-disable tailwindcss/no-custom-classname */
import React, {
  forwardRef, useContext, useState, useEffect, useMemo, Fragment, useRef, useCallback
} from 'react';
import {
  string, func, arrayOf, shape
} from 'prop-types';
import {
  QueryContext, QueryProvider, extend,
  params,
  string as stringType,
  number as numberType,
  customType,
} from '@thd-nucleus/data-sources';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import { CustomProductPodDetails, ProductPodGroup } from '@thd-olt-component-react/product-pod-group';
import { AppContext } from '@thd-nucleus/app-render';
import { Col } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { ResultsWrapped } from '../ResultsWrapped';
import { ProductResultsContext } from '../ProductResultsContext';
import { ScreenSize } from '../../product-results-helpers';
import { useProductsPerRow } from './hooks/useProductsPerRow';
import { useProductResults } from '../../hooks/useProductResults';
import useProductResultsParamsPLP from '../../hooks/useProductResultsParamsPLP';

const ProductResultsGrid = forwardRef(({
  pageType,
  defaultVariables,
  podFunction,
  slots,
  keyword,
  storeId,
}, ref) => {

  const {
    data: wrapperData,
    loading: wrapperLoading,
    responseLoading: wrapperResponseLoading,
    setData
  } = useContext(ProductResultsContext);

  const { isClientResolved } = useContext(QueryContext);
  const { channel, updateCustomContext } = useContext(ExperienceContext);
  const { instance: browseInstance } = useContext(AppContext);

  const isSearch = pageType === 'search';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const { isLocalized } = useStore({ varnish, online: false });
  const store = useStore({ varnish, online: false });

  const initialStoreRef = useRef();
  if (!initialStoreRef.current) {
    initialStoreRef.current = store?.storeId || storeId;
  }

  const storeRef = useRef({
    storeId: initialStoreRef.current
  });

  const initialIsControlledDataRef = useRef();
  if (initialIsControlledDataRef.current === undefined) {
    initialIsControlledDataRef.current = Boolean(
      (wrapperData && typeof wrapperData === 'object' && Object.keys(wrapperData).length > 0)
      || wrapperLoading
      || wrapperResponseLoading
    );
  }

  const isControlledData = initialIsControlledDataRef.current;

  if (typeof updateCustomContext === 'function') {
    const isBrowse = pageType === 'browse';
    updateCustomContext({ pageType, isSearch, varnish, isBrowse });
  }

  const queryOptions = useProductResultsParamsPLP({ pageType });
  const selfFetchedData = useProductResults({
    skip: isControlledData,
    ...queryOptions,
  });

  let data = isControlledData ? wrapperData : selfFetchedData.data;
  let loading = isControlledData ? wrapperLoading : selfFetchedData.loading;
  let responseLoading = isControlledData ? wrapperResponseLoading : selfFetchedData.responseLoading;

  if (!isControlledData) {
    if (loading || responseLoading) {
      // During loading: Use default store to prevent localization issues
      storeRef.current = {
        storeId: initialStoreRef.current
      };
    } else if (data && !loading && !responseLoading) {
      // Data loaded: Use localized store from current context
      storeRef.current = {
        storeId: store?.storeId || initialStoreRef.current
      };
      setData(data);
    }
  }

  const isSSRWithPlaceholdersEnabled = useConfigService('fs-prop:isSSRWithPlaceholdersEnabled');

  const showPlaceholderPodsFlag = !browseInstance?.thdSEOBotDetection
    && (isSSRWithPlaceholdersEnabled || (!isSSRWithPlaceholdersEnabled && !isSearch));
  const [shouldShowInitialPlaceholders, setShouldShowInitialPlaceholders] = useState(showPlaceholderPodsFlag);

  const isDesktop = channel === 'desktop';

  const { products = [] } = data?.searchModel || {};

  useEffect(() => {
    const showPlaceholders = loading || responseLoading;

    if (showPlaceholders !== shouldShowInitialPlaceholders) {
      setShouldShowInitialPlaceholders(showPlaceholders);
    }
  }, [loading, responseLoading]);

  const [productsPerRow, matchesScreen] = useProductsPerRow({ channel });

  const componentsToRender = useMemo(() => {
    let screen = matchesScreen.current;

    if ((matchesScreen.current === ScreenSize.DX_LARGE || typeof window === 'undefined')
      && channel === 'desktop') {
      screen = ScreenSize.X_LARGE;
    }

    return slots?.reduce((res, slot) => {
      const position = slot.position[screen] || slot.position[ScreenSize.UNPREFIXED];
      res[position] = slot.component;
      return res;
    }, []);
  }, [matchesScreen.current, slots]);

  const mountedFn = ({ mounted }) => {
    if (!isLocalized) {
      return false; // Don't mount if store not localized
    }
    if (responseLoading) {
      return false; // Don't mount during response loading
    }
    return mounted;
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip; // Only control product queries
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });

    if (attributes.product.fulfillment) {
      if (isResolved) {
        return skip; // Skip if search model resolved
      }
      return true; // Skip fulfillment queries until search model ready
    }
    return skip;
  };

  const defaultVariablesFallback = {
    storeId,
    skipKPF: true,
    skipSubscribeAndSave: true,
    skipInstallServices: false
  };

  const enhancedDefaultVariables = useMemo(() => {
    // If controlled data, use passed defaultVariables as-is (parent manages store)
    if (isControlledData) {
      return defaultVariables;
    }

    // If self-fetching, enhance with store ref for proper localization
    const baseVariables = {
      storeId: storeRef.current?.storeId || storeId,
      skipKPF: true,
      skipSubscribeAndSave: true,
      skipInstallServices: false
    };

    return defaultVariables ? { ...defaultVariables, ...baseVariables } : baseVariables;
  }, [isControlledData, defaultVariables, storeRef.current?.storeId]);
  /**
   * PRODUCT SETS CALCULATION: Groups products into sets for rendering with slot insertion
   *
   * Returns an array of objects containing:
   * - start: starting index for product pod loop
   * - productSetSize: how many products are grouped together
   * - slotIndex: the index for promotional/content component to be rendered in the slot
   *
   * This allows promotional content (ads, banners) to be inserted between product rows
   * while maintaining proper grid layout and responsive behavior.
   */
  const productSets = useMemo(() => {
    if (!products?.length) {
      return [];
    }

    const sets = [];
    let currentIndex = 0;

    // Insert slot components at calculated positions based on products per row
    if (componentsToRender?.length > 0) {
      componentsToRender.forEach((component, i) => {
        if (!component || currentIndex >= products.length) {
          return;
        }

        const nextPosition = i * productsPerRow;

        if (nextPosition <= currentIndex) {
          return;
        }

        sets.push({
          start: currentIndex,
          productSetSize: Math.min(nextPosition, products.length) - currentIndex,
          slotIndex: nextPosition <= products.length ? i : -1
        });

        currentIndex = nextPosition;
      });
    }

    // Add remaining products after all slots are placed
    while (currentIndex < products.length) {
      sets.push({
        start: currentIndex,
        productSetSize: Math.min(productsPerRow, products.length - currentIndex),
        slotIndex: -1 // No slot component for this set
      });

      currentIndex += productsPerRow;
    }

    return sets;
  }, [productsPerRow, products?.length, componentsToRender]);

  // SLOT RENDERING: Render promotional/content components at calculated positions
  const renderSlot = useCallback((slotIndex) => {
    return (
      <div className="sui-w-full" data-testid={`slot-${slotIndex}`}>
        {componentsToRender && componentsToRender[slotIndex]}
      </div>
    );
  }, [componentsToRender]);

  // PRODUCT POD RENDERING: Render product pods with proper grid layout and error boundaries
  const renderPods = useCallback((startIndex, numberOfPods) => {
    let podsToRender = numberOfPods || 0;
    if (products && startIndex + numberOfPods > products.length) {
      podsToRender = products.length - startIndex;
    }
    if (!products || podsToRender <= 0) {
      return null;
    }

    return (
      <ProductPodGroup
        DetailsPod={CustomProductPodDetails}
        keyword={keyword}
      >
        {Array.from({ length: podsToRender }).map((_elem, idx) => {
          const itemIndex = startIndex + idx;
          const itemId = products[itemIndex].identifiers?.itemId;
          return (
            <ErrorBoundary
              id={`plp-product-pod__${itemIndex}`}
              index={itemIndex}
              key={`${itemId}-${itemIndex}`}
              name={`Product Pod: ${itemIndex}`}
            >
              <Col
                className="browse-search__pod"
                fallback={isDesktop ? '6' : '12'}
                lg="3"
                md="3"
                sm="4"
                xs="6"
              >
                {podFunction(products[itemIndex], itemIndex)}
              </Col>
            </ErrorBoundary>
          );
        })}
      </ProductPodGroup>
    );
  }, [products, keyword, isDesktop, podFunction]);

  return (
    <ResultsWrapped ref={ref}>
      <div id="plp-grid" className="grid" data-testid="plp-grid">
        {/* PHASE 1: Initial placeholders (12 pods) shown during loading */}
        {shouldShowInitialPlaceholders && (<ProductPodPlaceholder podsCount={12} />)}

        {/* PHASE 2: Actual products with slot components when data loaded */}
        {!!products?.length && !shouldShowInitialPlaceholders && (
          <QueryProvider
            cacheKey={`search-product-pods-${isDesktop ? 'desktop' : 'mobile'}`}
            dataSource="searchNav"
            defaultVariables={enhancedDefaultVariables || defaultVariablesFallback}
            mounted={mountedFn}
            skip={skipFn}
          >
            {productSets?.map((productSet) => {
              return (
                <Fragment key={`productSet-${productSet.start}-${productSet.productSetSize}`}>
                  {renderPods(productSet.start, productSet.productSetSize)}
                  {renderSlot(productSet.slotIndex)}
                </Fragment>
              );
            })}
          </QueryProvider>
        )}
      </div>
    </ResultsWrapped>
  );
});

const createGridWithPod = (CustomPod) => {
  ProductPodGroup.dataModel = extend(ProductPodGroup.dataModel || {},
    CustomPod,
    CustomProductPodDetails);

  ProductResultsGrid.dataModel = extend({
    searchModel: params(
      {
        keyword: stringType(),
        navParam: stringType(),
        channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP'),
        storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),

      }
    ).shape({
      id: stringType(),
      products: params({ startIndex: numberType() }).arrayOf(CustomPod.dataModel.product),
    })
  },
  ProductPodGroup,
  );

  return ProductResultsGrid;
};

ProductResultsGrid.displayName = 'ProductResultsGrid';
ProductResultsGrid.propTypes = {
  defaultVariables: shape({}),
  podFunction: func.isRequired,
  pageType: string,
  keyword: string,
  storeId: string,
  slots: arrayOf(shape({}))
};

ProductResultsGrid.defaultProps = {
  pageType: null,
  keyword: null,
  defaultVariables: null,
  slots: null,
  storeId: null
};
export { createGridWithPod };
